import React, { useEffect, useRef } from 'react';
import { MessageCircle, AtSign, X } from 'lucide-react';
import { useChatPopup } from '../../contexts/ChatPopupContext';
import { useChat } from '../../contexts/ChatContext';
import './ChatToastNotifications.css';

const AUTO_DISMISS_MS = 6000;

export const ChatToastNotifications: React.FC = () => {
  const { toasts, dismissToast, openChat, maximizeChat } = useChatPopup();
  const { selectRoom } = useChat();
  const scheduledRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    toasts.forEach(toast => {
      if (scheduledRef.current.has(toast.id)) return;
      scheduledRef.current.add(toast.id);
      setTimeout(() => {
        scheduledRef.current.delete(toast.id);
        dismissToast(toast.id);
      }, AUTO_DISMISS_MS);
    });
  }, [toasts, dismissToast]);

  // Notificação de chat clicada no sino (Header): abre o popup na sala.
  useEffect(() => {
    const handleOpenRoom = (event: Event) => {
      const roomId = (event as CustomEvent<{ roomId?: string }>).detail?.roomId;
      if (!roomId) return;
      openChat();
      maximizeChat();
      selectRoom(roomId).catch(err => console.error('ChatToastNotifications: Erro ao abrir sala', err));
    };
    window.addEventListener('chat:open-room', handleOpenRoom);
    return () => window.removeEventListener('chat:open-room', handleOpenRoom);
  }, [openChat, maximizeChat, selectRoom]);

  if (toasts.length === 0) return null;

  const handleOpen = (roomId: string, toastId: string) => {
    openChat();
    maximizeChat();
    selectRoom(roomId).catch(err => console.error('ChatToastNotifications: Erro ao abrir sala', err));
    dismissToast(toastId);
  };

  return (
    <div className="chat-toast-notifications">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`chat-toast ${toast.type === 'mention' ? 'mention' : 'message'}`}
          onClick={() => handleOpen(toast.roomId, toast.id)}
        >
          <div className="chat-toast-icon">
            {toast.type === 'mention' ? (
              <AtSign className="w-4 h-4" />
            ) : (
              <MessageCircle className="w-4 h-4" />
            )}
          </div>

          <div className="chat-toast-content">
            <div className="chat-toast-header">
              <span className="chat-toast-sender">{toast.senderName}</span>
              <span className="chat-toast-room">{toast.roomName}</span>
            </div>
            <div className="chat-toast-message">
              {toast.content.length > 80 ? `${toast.content.substring(0, 80)}...` : toast.content}
            </div>
          </div>

          <button
            className="chat-toast-dismiss"
            onClick={(e) => {
              e.stopPropagation();
              dismissToast(toast.id);
            }}
            title="Dispensar"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default ChatToastNotifications;
